import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { History, RotateCcw, Loader2, Save, Timer, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { invokeErrorMessage } from "@/lib/invokeErrorMessage";
import {
  describeSnapshot,
  snapshotKindLabel,
  snapshotWasPublished,
  sortSnapshotsNewestFirst,
  type ScheduleSnapshot,
} from "@/lib/scheduleSnapshots";

interface ScheduleHistoryButtonProps {
  briefingId: string;
  /**
   * Called after a successful restore. The schedule editor holds the
   * assignments in local state loaded once from the server, so the caller
   * reloads to render the restored data cleanly (a restore is a rare,
   * deliberate action — a reload is the safe, predictable refresh).
   */
  onRestored?: () => void;
  /**
   * Snapshots the editor's CURRENT (possibly unsaved) draft before the
   * restore overwrites it, so the follow-up reload never silently loses
   * in-progress edits — they remain recoverable from history. Best-effort:
   * a failure here doesn't block the restore (the DB pre-restore snapshot
   * still captures the last published state).
   */
  onBeforeRestore?: () => Promise<void>;
}

/**
 * Self-contained "History" affordance for the schedule. Lists point-in-time
 * snapshots (written on every save by the save-schedule-changes edge function)
 * and lets a producer restore one. Kept as its own component so the giant
 * CrewSchedule only needs a single button + this dialog.
 */
export function ScheduleHistoryButton({
  briefingId,
  onRestored,
  onBeforeRestore,
}: ScheduleHistoryButtonProps) {
  const [open, setOpen] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const { data: snapshots = [], isLoading } = useQuery({
    queryKey: ["schedule_snapshots", briefingId],
    enabled: !!briefingId && open,
    // While the dialog is open, keep the list live so autosaves (written every
    // ~20s) appear on their own — no manual refresh. `enabled` already gates on
    // `open`, so a disabled query polls not at all.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ScheduleSnapshot[]> => {
      const { data, error } = await supabase
        .from("schedule_snapshots")
        .select("id, briefing_id, created_at, created_by, actor_name, kind, block_count")
        .eq("briefing_id", briefingId)
        .order("created_at", { ascending: false })
        // Above the combined retention (40 manual + 20 auto = 60) so the
        // oldest retained snapshots are still visible/restorable.
        .limit(100);
      if (error) throw error;
      return sortSnapshotsNewestFirst((data ?? []) as ScheduleSnapshot[]);
    },
  });

  const handleRestore = async (snapshotId: string) => {
    setRestoringId(snapshotId);
    try {
      // Preserve unsaved editor edits in history before the restore + reload
      // discards them. Best-effort: never let it block the restore itself.
      if (onBeforeRestore) {
        try {
          await onBeforeRestore();
        } catch (err) {
          console.error("[history] pre-restore draft capture failed:", err);
        }
      }
      const { error } = await supabase.functions.invoke("restore-schedule-snapshot", {
        body: { snapshot_id: snapshotId },
      });
      if (error) throw new Error(await invokeErrorMessage(error));
      toast.success("Schedule restored", {
        description: "Your current version was saved to history first.",
      });
      setOpen(false);
      onRestored?.();
    } catch (err) {
      toast.error("Couldn't restore this version", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        onClick={() => setOpen(true)}
        title="Schedule history"
        aria-label="Schedule history"
      >
        <History className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule history</DialogTitle>
            <DialogDescription>
              Every save is captured here. Restoring a version saves your current one to history
              first, so you can always switch back.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[420px] space-y-2 overflow-y-auto overscroll-contain pr-1">
            {isLoading && (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            )}

            {!isLoading && snapshots.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No saved versions yet. They appear here after your next save.
              </p>
            )}

            {!isLoading &&
              snapshots.map((snap) => {
                const isAuto = snap.kind === "auto";
                const isPreRestore = snap.kind === "pre_restore";
                const KindIcon = isAuto ? Timer : isPreRestore ? Undo2 : Save;
                return (
                  <div
                    key={snap.id}
                    className={`flex items-center justify-between gap-3 rounded-md border p-3 ${
                      isAuto ? "border-dashed border-border bg-muted/30" : "border-border"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{describeSnapshot(snap)}</p>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <KindIcon className="h-3 w-3" />
                        {snapshotKindLabel(snap.kind)}
                        {!snapshotWasPublished(snap.kind) && (
                          <span className="italic"> · draft, not sent to crew</span>
                        )}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={restoringId !== null}
                      onClick={() => handleRestore(snap.id)}
                    >
                      {restoringId === snap.id ? (
                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <RotateCcw className="mr-1 h-3 w-3" />
                      )}
                      Restore
                    </Button>
                  </div>
                );
              })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
